import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  CircularProgress,
  DialogContent,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Typography
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Dialog from 'src/components/Dialog/Dialog';
import Autocomplete from 'src/components/Autocomplete/Autocomplete';
import { getClientById } from 'src/services/clientService';
import {
  loadAllClients,
  LOAD_CLIENTS
} from 'src/store/actions/clientActions';
import {
  editBureauExclusionsParameter,
  EDIT_BUREAU_EXCLUSIONS_PARAMETER
} from 'src/store/actions/parameterActions';
import { selectClientsList } from 'src/store/selectors/clientSelectors';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import { transformJsonToArray, transformArrayToString } from 'src/utils/common';

const BureauExclusionsModal = ({ open, handleClose, parameter }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const allClients = useSelector(selectClientsList);
  const isLoadingClients = useSelector(
    isLoadingSelector([LOAD_CLIENTS.action])
  );

  const [initialIds, setInitialIds] = useState([]);
  const [excludedClients, setExcludedClients] = useState([]);
  const [loadingExcluded, setLoadingExcluded] = useState(false);
  const loadingSave = useSelector(
    isLoadingSelector([EDIT_BUREAU_EXCLUSIONS_PARAMETER.action])
  );

  useEffect(() => {
    if (!open || !parameter) {
      setExcludedClients([]);
      setInitialIds([]);
      return;
    }

    dispatch(loadAllClients());

    const ids = transformJsonToArray(parameter.value).filter(Boolean);
    setInitialIds(ids);

    if (ids.length === 0) {
      setExcludedClients([]);
      return;
    }

    const loadClientNames = async () => {
      setLoadingExcluded(true);
      try {
        const clients = await Promise.all(
          ids.map(id =>
            getClientById(id)
              .then(c => ({
                id: String(c.id),
                fullName: c.fullName || String(id)
              }))
              .catch(() => ({ id: String(id), fullName: String(id) }))
          )
        );
        setExcludedClients(clients);
      } finally {
        setLoadingExcluded(false);
      }
    };
    loadClientNames();
  }, [open, parameter]);

  const clientOptions = useMemo(() => {
    const excludedIds = new Set(excludedClients.map(c => c.id));
    return (allClients || [])
      .filter(c => !excludedIds.has(String(c.id)))
      .map(c => ({
        id: String(c.id),
        fullName: c.fullName || String(c.id),
        label: `${c.id} - ${c.fullName || ''}`
      }));
  }, [allClients, excludedClients]);

  const handleAdd = useCallback(client => {
    if (!client) return;
    setExcludedClients(prev => [
      { id: client.id, fullName: client.fullName },
      ...prev
    ]);
  }, []);

  const handleRemove = useCallback(client => {
    setExcludedClients(prev => prev.filter(c => c.id !== client.id));
  }, []);

  const handleSave = useCallback(async () => {
    const currentIds = excludedClients.map(c => c.id);
    const hasChanges =
      currentIds.length !== initialIds.length ||
      currentIds.some(id => !initialIds.includes(id)) ||
      initialIds.some(id => !currentIds.includes(id));

    if (!hasChanges) {
      handleClose();
      return;
    }

    dispatch(
      editBureauExclusionsParameter(
        { ...parameter, value: transformArrayToString(currentIds) },
        handleClose
      )
    );
  }, [excludedClients, initialIds, parameter, dispatch, handleClose]);

  if (!open) return null;

  return (
    <Dialog
      open={open}
      handleClose={handleClose}
      title={t('PARAMETERS_VIEW.UPDATE_PARAMETER_TITLE')}
      maxWidth="md"
      fullWidth
      sx={{ '& .MuiDialog-paper': { width: '700px', maxWidth: '90vw' } }}
    >
      <DialogContent>
        <Box mb={3} sx={{ width: '100%' }}>
          <Typography variant="h6" marginBottom={2} gutterBottom>
            {t('PARAMETER_VIEW.BURO_EXCLUDED_CLIENTS')}
          </Typography>

          <Autocomplete
            options={clientOptions}
            value={null}
            getOptionLabel={option => option.label || ''}
            isOptionEqualToValue={(option, val) => option.id === val.id}
            isLoading={isLoadingClients}
            noOptionsText={t('COMMON.NO_DATA')}
            label={t('COMMON.CLIENT')}
            onChange={(_e, val) => {
              if (val) handleAdd(val);
            }}
            sx={{ marginBottom: 2 }}
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box mb={3}>
          <Typography variant="h6" gutterBottom marginBottom={2}>
            {`${t('PARAMETER_VIEW.BURO_EXCLUDED_CLIENTS')} (${excludedClients.length})`}
          </Typography>

          {loadingExcluded && <CircularProgress size={24} />}

          {!loadingExcluded && excludedClients.length === 0 && (
            <Typography variant="body2" color="textSecondary">
              {t('PARAMETERS_VIEW.NO_BUREAU_EXCLUDED_CLIENTS')}
            </Typography>
          )}

          {!loadingExcluded && excludedClients.length > 0 && (() => {
            const initialIdSet = new Set(initialIds);
            const newClients = excludedClients.filter(
              c => !initialIdSet.has(c.id)
            );
            const existingClients = excludedClients.filter(c =>
              initialIdSet.has(c.id)
            );

            const renderItem = client => (
              <ListItem key={client.id}>
                <ListItemText
                  primary={client.fullName}
                  secondary={`${t('COMMON.ID')}: ${client.id}`}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => handleRemove(client)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            );

            return (
              <Box
                sx={{ maxHeight: 280, overflowY: 'auto', overflowX: 'hidden' }}
              >
                {newClients.length > 0 && (
                  <>
                    <Typography
                      variant="subtitle2"
                      color="primary"
                      sx={{ px: 1, py: 0.5 }}
                    >
                      {`${t('PARAMETERS_VIEW.BUREAU_TO_EXCLUDE')} (${newClients.length})`}
                    </Typography>
                    <List dense disablePadding>
                      {newClients.map(renderItem)}
                    </List>
                  </>
                )}

                {newClients.length > 0 && existingClients.length > 0 && (
                  <Divider sx={{ my: 1 }} />
                )}

                {existingClients.length > 0 && (
                  <>
                    <Typography
                      variant="subtitle2"
                      color="textSecondary"
                      sx={{ px: 1, py: 0.5 }}
                    >
                      {`${t('PARAMETERS_VIEW.BUREAU_ALREADY_EXCLUDED')} (${existingClients.length})`}
                    </Typography>
                    <List dense disablePadding>
                      {existingClients.map(renderItem)}
                    </List>
                  </>
                )}
              </Box>
            );
          })()}
        </Box>

        <Box display="flex" justifyContent="space-between" gap={2}>
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{ width: '100%' }}
            disabled={loadingSave}
          >
            {t('COMMON.CANCEL')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            sx={{ width: '100%' }}
            disabled={loadingSave || loadingExcluded}
            onClick={handleSave}
          >
            {loadingSave ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t('COMMON.SAVE')
            )}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

BureauExclusionsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  parameter: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    value: PropTypes.string,
    type: PropTypes.string
  })
};

export default BureauExclusionsModal;
